import { ONBOARDING_STATUS } from "../constants/onboarding";
import {
  CANONICAL_FIELD_NAME_ALIASES_TO_JEDI_UI,
  JEDI_UI_TO_CANONICAL_FIELD_NAMES,
  resolveCanonicalFieldNames,
} from "../constants/onboardingFeedbackFields";
import { JOB_DETAILS_OPTIONS } from "../data/jobDetailsConfig";
import {
  getEducationDocumentCanonicalFieldName,
  getEducationDocumentLabels,
  getEducationQualificationLabel,
} from "./educationHelpers";
import moment from "moment";

/** Sections included in save_progress approval_feedback.
 * `job_details` is always present as [] — Flag/Correction is not supported there.
 */
const FEEDBACK_SECTION_KEYS = [
  "personal",
  "address",
  "education",
  "employment",
  "bank",
  "insurance",
  "declaration",
  "job_details",
];

/** send_back_for_correction omits job_details from approval_feedback */
const SEND_BACK_FEEDBACK_SECTION_KEYS = [
  "personal",
  "address",
  "education",
  "employment",
  "bank",
  "insurance",
  "declaration",
];

/** Map UI section keys → API approval_feedback keys (no jobDetails — always empty) */
const UI_SECTION_TO_FEEDBACK_KEY = {
  personal: "personal",
  address: "address",
  education: "education",
  employment: "employment",
  bank: "bank",
  insurance: "insurance",
  declarations: "declaration",
};

const STATUS_ALIASES = {
  pending: ONBOARDING_STATUS.PENDING,
  pending_review: ONBOARDING_STATUS.PENDING,
  submitted: ONBOARDING_STATUS.PENDING,
  in_progress: ONBOARDING_STATUS.IN_PROGRESS,
  inprogress: ONBOARDING_STATUS.IN_PROGRESS,
  awaiting: ONBOARDING_STATUS.AWAITING,
  awaiting_resubmission: ONBOARDING_STATUS.AWAITING,
  resubmitted: ONBOARDING_STATUS.RESUBMITTED,
  completed: ONBOARDING_STATUS.COMPLETED,
  complete: ONBOARDING_STATUS.COMPLETED,
};

export const emptyApprovalFeedback = (sectionKeys = FEEDBACK_SECTION_KEYS) =>
  sectionKeys.reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});

export const normalizeOnboardingStatus = (status) => {
  if (!status) return ONBOARDING_STATUS.PENDING;
  const key = String(status).trim().toLowerCase().replace(/\s+/g, "_");
  return STATUS_ALIASES[key] || key;
};

/** Case-insensitive match for summary API status "Re-submitted". */
export const isResubmittedSummaryStatus = (status) => {
  if (status == null || String(status).trim() === "") return false;
  const normalized = String(status)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
  return normalized === "re_submitted" || normalized === "resubmitted";
};

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  // Actual onboarding summary shape: { status, message, data: [...] }
  if (Array.isArray(value.data)) return value.data;
  if (Array.isArray(value.list)) return value.list;
  if (Array.isArray(value.items)) return value.items;
  if (Array.isArray(value.candidates)) return value.candidates;
  if (Array.isArray(value.summary)) return value.summary;
  if (Array.isArray(value.results)) return value.results;
  return [];
};

const getResponseBody = (response) => {
  if (!response || typeof response !== "object") return {};

  // API Gateway style: { statusCode, headers, body: { status, data } }
  if (response.body != null) {
    return parseMaybeJson(response.body) || {};
  }

  // Direct style: { status, message, data }
  if (Array.isArray(response.data) || (response.data && typeof response.data === "object")) {
    // Prefer nested business payload when present
    if (response.status != null || response.message != null) {
      return response;
    }
    return response.data;
  }

  return response;
};

/**
 * Ensure backend onboarding_id is always sent as a number.
 * If API returns "40" as a string, we convert it to 40.
 */
export const normalizeOnboardingId = (value) => {
  if (value == null) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return Number(trimmed);
  }
  return value;
};

/**
 * Normalize get_candidate_approval_summary into Review Queue rows.
 * Expected: { statusCode, body: { status: "SUCCESS", data: [...] } }
 */
export const normalizeApprovalSummaryList = (response) => {
  const body = getResponseBody(response);
  const list = Array.isArray(body) ? body : asArray(body);

  return list.map((item, index) => {
    // Backend returns `onboarding_id` for each onboarding request.
    // Keep fallbacks only to make the UI resilient to shape differences.
    const onboardingId =
      item?.onboarding_id ??
      item?.onboardingId ??
      item?.onboarding_request_id ??
      item?.id ??
      item?.request_id ??
      item?.requestId;

    const id = onboardingId != null ? String(onboardingId) : `REQ-${index}`;

    // Prefer sortable timestamp; keep pre-formatted relative text separately
    const submittedAt =
      item?.create_date ||
      item?.created_at ||
      item?.submitted_at ||
      item?.submittedAt ||
      item?.submitted_on ||
      item?.updated_at ||
      null;

    return {
      id,
      candidate_name:
        item?.candidate_name ||
        item?.candidateName ||
        item?.name ||
        item?.full_name ||
        "Candidate",
      designation: item?.designation || item?.role || item?.job_title || "-",
      store_code: item?.store_code || item?.storeCode || item?.location_code || "-",
      store_name: item?.store_name || item?.storeName || item?.location_name || "",
      aadhaar_number:
        item?.aadhaar_number == null ||
        String(item.aadhaar_number).trim() === ""
          ? ""
          : String(item.aadhaar_number).trim(),
      submitted_at: submittedAt,
      submitted_label: item?.submitted || item?.submitted_label || null,
      // Keep backend status as-is for Review Queue display/filter
      status:
        item?.status == null || String(item.status).trim() === ""
          ? ""
          : String(item.status),
      onboarding_id: normalizeOnboardingId(onboardingId ?? null),
      raw: item,
    };
  });
};

/**
 * Mask Aadhaar for display (last 4 digits only), e.g. XXXXXXXX1234.
 * Search/filter should keep using the full value separately.
 */
export const maskAadhaarNumber = (aadhaarNumber = "") => {
  const digits = String(aadhaarNumber || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length <= 4) return digits;
  return `${"X".repeat(digits.length - 4)}${digits.slice(-4)}`;
};

/**
 * Derive unique stores from summary rows for the store filter.
 */
export const buildStoresFromSummary = (requests = []) => {
  const map = new Map();
  requests.forEach((request) => {
    const code = request?.store_code;
    if (!code || code === "-" || map.has(code)) return;
    map.set(code, {
      store_code: code,
      store_name: request?.store_name || "",
    });
  });
  return Array.from(map.values());
};

/**
 * Resolve the candidate detail object from API Gateway / business envelope.
 * Actual shape: { statusCode, body: { status, data: { personal, ... } } }
 */
export const getApprovalDetailsPayload = (response) => {
  const body = getResponseBody(response);

  if (body?.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return body.data;
  }
  if (body?.candidate && typeof body.candidate === "object") return body.candidate;
  if (body?.details && typeof body.details === "object") return body.details;
  if (body?.detail && typeof body.detail === "object") return body.detail;

  // Avoid treating { status, message, data: [...] } summary envelopes as detail
  if (body?.status && body?.data !== undefined && !body?.personal) {
    return {};
  }

  return typeof body === "object" && !Array.isArray(body) ? body : {};
};

const DATE_PARSE_FORMATS = [
  moment.ISO_8601,
  "YYYY-MM-DD",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DDTHH:mm:ssZ",
  "DD-MM-YYYY",
  "DD-MM-YYYY hh:mm A",
  "DD MMM YYYY, hh:mm A",
];

const parseMomentDate = (value) => {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const strict = moment(raw, DATE_PARSE_FORMATS, true);
  if (strict.isValid()) return strict;
  const loose = moment(raw);
  return loose.isValid() ? loose : null;
};

const formatDisplayDateTime = (value) => {
  const parsed = parseMomentDate(value);
  return parsed ? parsed.format("DD-MM-YYYY hh:mm A") : value == null ? "" : String(value).trim();
};

const isVerifiedAtFieldKey = (fieldKey = "") =>
  /(^|_)verified_at$/i.test(String(fieldKey || "").trim());

const isDateOnlyFieldKey = (fieldKey = "") => {
  const key = String(fieldKey || "").trim();
  if (!key || isVerifiedAtFieldKey(key)) return false;
  return /(_date$|^date_|_dob$|date_of_)/i.test(key);
};

/** Display-only formatting for ReviewRequest fields. Does not mutate stored values. */
export const formatReviewFieldDisplayValue = (fieldKey, value) => {
  if (value == null || value === "") return value ?? "";
  if (String(fieldKey || "").trim() === "qualification") {
    return getEducationQualificationLabel(value);
  }
  const parsed = parseMomentDate(value);
  if (!parsed) return value;

  if (isVerifiedAtFieldKey(fieldKey)) {
    return parsed.format("DD-MM-YYYY hh:mm A");
  }
  if (isDateOnlyFieldKey(fieldKey)) {
    return parsed.format("DD-MM-YYYY");
  }
  return value;
};

const mapGender = (value) => {
  if (value == null || value === "") return "";
  const key = String(value).trim().toUpperCase();
  if (key === "M" || key === "MALE") return "Male";
  if (key === "F" || key === "FEMALE") return "Female";
  if (key === "O" || key === "OTHER") return "Other";
  return String(value);
};

const readAddressValue = (block, key) => {
  if (!block || typeof block !== "object") return "";
  if (!Object.prototype.hasOwnProperty.call(block, key)) return "";
  const value = block[key];
  return value == null ? "" : String(value);
};

const mapAddressBlock = (block = {}, prefix) => ({
  [`${prefix}_address_house`]: readAddressValue(block, "house"),
  [`${prefix}_address_street`]: readAddressValue(block, "street"),
  [`${prefix}_address_area`]: readAddressValue(block, "area"),
  [`${prefix}_address_landmark`]: readAddressValue(block, "landmark"),
  [`${prefix}_address_city`]: readAddressValue(block, "city"),
  [`${prefix}_address_state`]: readAddressValue(block, "state"),
  [`${prefix}_address_pincode`]:
    readAddressValue(block, "pincode") || readAddressValue(block, "pin_code"),
});

const mapPersonalSection = (personal = {}) => {
  const aadhaar = personal.aadhaar || personal.aadhar || {};
  return {
    aadhar_number: aadhaar.number || personal.aadhar_number || "",
    aadhar_verified_at: formatDisplayDateTime(
      aadhaar.verified_at || personal.aadhar_verified_at,
    ),
    first_name: personal.first_name || "",
    middle_name: personal.middle_name || "",
    last_name: personal.last_name || "",
    father_first_name: personal.father_first_name || "",
    father_middle_name: personal.father_middle_name || "",
    father_last_name: personal.father_last_name || "",
    date_of_birth: personal.date_of_birth || "",
    gender: mapGender(personal.gender),
    phone_number:
      personal.personal_mobile_no ||
      personal.phone_number ||
      personal.mobile ||
      "",
    alternative_phone_number: personal.office_mobile_no || "",
    email_address:
      personal.personal_email_id || personal.email_address || personal.email || "",
    marital_status: personal.marital_status || "",
    nationality: personal.nationality || "",
    full_name: personal.full_name || "",
  };
};

const mapAddressSection = (address = {}) => {
  const current = address.current_address || address.current || {};
  const permanent = address.permanent_address || address.permanent || {};
  const mapped = {
    ...mapAddressBlock(current, "current"),
    ...mapAddressBlock(permanent, "permanent"),
  };

  const comparedKeys = [
    "house",
    "street",
    "area",
    "landmark",
    "city",
    "state",
    "pincode",
  ];
  const sameByValue = comparedKeys.every((key) => {
    const currentValue = String(mapped[`current_address_${key}`] ?? "")
      .trim()
      .toLowerCase();
    const permanentValue = String(mapped[`permanent_address_${key}`] ?? "")
      .trim()
      .toLowerCase();
    return currentValue === permanentValue;
  });

  // current_address.address_flag is the backend same-address signal when present.
  const addressFlag = current.address_flag;
  const same =
    addressFlag === true || addressFlag === false ? addressFlag : sameByValue;

  return {
    ...mapped,
    address_flag: addressFlag,
    same_as_current: same,
  };
};

const mapEducationCourseType = (courseType) => {
  const raw = String(courseType || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "12th" || raw === "twelfth") return "12th";
  if (raw === "10th" || raw === "tenth") return "10th";
  if (raw === "graduate" || raw === "graduation") return "graduate";
  if (raw.includes("post")) return "post_graduate";
  if (raw.includes("diploma")) return "diploma";
  if (raw.includes("doctor") || raw === "phd") return "doctorate";
  return raw.replace(/\s+/g, "_");
};

const mapEducationSection = (education) => {
  const recordsSource = Array.isArray(education)
    ? education
    : Array.isArray(education?.records)
      ? education.records
      : [];

  const records = recordsSource.map((item) => {
    const qualification = mapEducationCourseType(
      item.course_type || item.qualification || item.level,
    );
    const combinedCgpa = String(item.cgpa_percentage || "");
    const combinedParts = combinedCgpa.includes("/")
      ? combinedCgpa.split("/")
      : [combinedCgpa, ""];
    const gpa =
      item.gpa ?? (combinedParts[0] !== "" ? combinedParts[0] : "");
    const maxGpa =
      item.max_gpa ??
      item.maximum_cgpa_percentage ??
      (combinedParts[1] || "");

    return {
      qualification,
      degree: item.education_degree || item.degree || "",
      start_date: item.start_date || "",
      graduation_date: item.end_date || item.graduation_date || "",
      university: item.university_name || item.university || "",
      college: item.institute_name || item.college || "",
      cgpa_percentage: gpa !== "" && gpa != null ? String(gpa) : "",
      maximum_cgpa_percentage:
        maxGpa !== "" && maxGpa != null ? String(maxGpa) : "",
      highest_qualification: !!item.highest_qualification,
      marksheets_s3_key: item.marksheets_s3_key || "",
      certificate_s3_key: item.certificate_s3_key || "",
    };
  });

  const highest =
    records.find((item) => item.highest_qualification)?.qualification ||
    records[0]?.qualification ||
    "";

  return {
    highest_qualification: highest,
    records,
  };
};

const mapEmploymentRecord = (item = {}) => {
  const isCurrent = !!(
    item.current_check ||
    item.is_current ||
    item.is_last_experience
  );

  return {
    company: item.company_name || item.company || item.employer_name || "",
    designation: item.job_title || item.designation || item.role || "",
    start_date: item.start_date || "",
    last_date: item.end_date || "",
    current_check: isCurrent,
    is_last_experience: isCurrent,
    job_location: item.job_location || "",
    job_summary: item.job_summary || "",
  };
};

const mapEmploymentSection = (employment = {}) => {
  const history = Array.isArray(employment.job_history)
    ? employment.job_history
    : Array.isArray(employment.records)
      ? employment.records
      : [];

  return {
    uan: employment.uan_number || employment.uan || "",
    uan_verified: employment.uan_verified,
    uan_verified_at: formatDisplayDateTime(employment.uan_verified_at),
    records: history.map(mapEmploymentRecord),
  };
};

const mapBankSection = (bank = {}) => ({
  account_holder_name: bank.name_as_per_pan || bank.account_holder_name || "",
  name_as_per_bank: bank.name_as_per_bank || "",
  pan_number: bank.pan_number || "",
  pan_verified_at: formatDisplayDateTime(bank.pan_verified_at),
  account_number: bank.bank_account_number || bank.account_number || "",
  bank_verified_at: formatDisplayDateTime(
    bank.pennyless_verified_at || bank.bank_verified_at,
  ),
  ifsc_code: bank.ifsc_code || "",
  account_type: bank.bank_account_type || bank.account_type || "",
  bank_name: bank.bank_name || "",
  branch_name: bank.branch_name || "",
  branch_address: bank.bank_branch_address || bank.branch_address || "",
});

const mapInsuranceSection = (insurance = {}) => {
  const emergency = insurance.emergency_contact || {};
  const nominee = insurance.nischint_nominee || insurance.nominee || {};
  return {
    blood_group: insurance.blood_group || "",
    nominee_full_name: nominee.name || insurance.nominee_full_name || "",
    nominee_relationship:
      nominee.relationship || insurance.nominee_relationship || "",
    nominee_dob: nominee.date_of_birth || insurance.nominee_dob || "",
    emergency_relationship:
      emergency.relationship || insurance.emergency_relationship || "",
    emergency_full_name: emergency.name || insurance.emergency_full_name || "",
    emergency_phone:
      emergency.mobile ||
      emergency.phone ||
      insurance.emergency_phone ||
      "",
    emergency_email: emergency.email || insurance.emergency_email || "",
    emergency_alternative_mobile: emergency.alternative_mobile || "",
  };
};

const mapPolicyAck = (policy) => {
  if (!policy || typeof policy !== "object") {
    return { value: "", verified_at: "" };
  }
  if (policy.acknowledged === true || policy.acknowledged === "true") {
    return {
      value: "Acknowledged",
      verified_at: formatDisplayDateTime(policy.acknowledged_at),
    };
  }
  if (typeof policy === "string") {
    return { value: policy, verified_at: "" };
  }
  return {
    value: policy.acknowledged ? String(policy.acknowledged) : "",
    verified_at: formatDisplayDateTime(policy.acknowledged_at),
  };
};

const mapDeclarationsSection = (declaration = {}) => {
  const policies = declaration.policy_acknowledgements || {};
  const code = mapPolicyAck(policies.code_of_conduct);
  const it = mapPolicyAck(policies.it_policy);
  const posh = mapPolicyAck(policies.posh_policy);

  return {
    code_of_conduct: code.value || declaration.code_of_conduct || "",
    code_of_conduct_verified_at:
      code.verified_at || declaration.code_of_conduct_verified_at || "",
    it_policy: it.value || declaration.it_policy || "",
    it_policy_verified_at:
      it.verified_at || declaration.it_policy_verified_at || "",
    posh_policy: posh.value || declaration.posh_policy || "",
    posh_policy_verified_at:
      posh.verified_at || declaration.posh_policy_verified_at || "",
  };
};

const mapJobDetailsSection = (jobDetails = {}) => {
  const mapped = { ...jobDetails, misc: jobDetails.misc || {} };
  // Normalize nulls to empty strings for controlled inputs
  Object.keys(mapped).forEach((key) => {
    if (key === "misc") return;
    if (mapped[key] == null) mapped[key] = "";
  });
  return mapped;
};

const getFileTypeFromKey = (key = "") => {
  const lower = String(key).toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (/\.(png|jpe?g|gif|webp)$/.test(lower)) return "img";
  if (/\.(doc|docx)$/.test(lower)) return "doc";
  return "pdf";
};

const buildDocFromS3 = (id, name, s3Key) => {
  const key = String(s3Key || "").trim();
  if (!key) return null;
  return {
    id,
    name,
    type: getFileTypeFromKey(key),
    size: "",
    url: key,
    s3_key: key,
    file_key: key,
  };
};

/**
 * Employment form docs (form2 / form11 / etc.) are shown only when:
 * - status is "uploaded" (case-insensitive), and
 * - s3_key has a non-empty value
 */
const buildEmploymentFormDoc = (id, name, formPayload) => {
  const form =
    formPayload && typeof formPayload === "object" && !Array.isArray(formPayload)
      ? formPayload
      : null;
  if (!form) return null;

  const status = String(form.status ?? "").trim().toLowerCase();
  if (status !== "uploaded") return null;

  return buildDocFromS3(id, name, form.s3_key);
};

/**
 * Extract a usable HTTPS URL from generate_presign_url API responses.
 * Supports plain strings and common envelope shapes (body.data / url fields).
 */
export const extractPresignedUrl = (response) => {
  const asHttpUrl = (value) => {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    return /^https?:\/\//i.test(trimmed) ? trimmed : "";
  };

  if (!response) return "";

  const direct = asHttpUrl(response);
  if (direct) return direct;

  const body = getResponseBody(response);
  const candidates = [
    body?.data,
    body?.url,
    body?.presigned_url,
    body?.presign_url,
    body?.signed_url,
    response?.data,
    response?.url,
    response?.presigned_url,
    response?.presign_url,
    response?.signed_url,
  ];

  for (const candidate of candidates) {
    const url = asHttpUrl(candidate);
    if (url) return url;

    if (candidate && typeof candidate === "object") {
      const nested = asHttpUrl(
        candidate.url ||
          candidate.presigned_url ||
          candidate.presign_url ||
          candidate.signed_url ||
          candidate.file_url,
      );
      if (nested) return nested;
    }
  }

  return asHttpUrl(typeof body === "string" ? body : "");
};

/**
 * Resolve the S3 file_key for generate_presign_url from a document card object.
 */
export const resolveDocumentFileKey = (doc = {}) => {
  const candidates = [doc.file_key, doc.s3_key, doc.key, doc.url];
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (!value) continue;
    // Absolute URLs are already viewable; they are not S3 keys.
    if (/^(https?:|blob:|data:)/i.test(value)) continue;
    return value;
  }
  return "";
};

/** Employment form document definitions keyed by API field name */
const EMPLOYMENT_FORM_DOC_DEFS = [
  { apiKey: "form2", id: "doc-form2", name: "Form 2.pdf" },
  { apiKey: "form11", id: "doc-form11", name: "Form 11.pdf" },
  {
    apiKey: "pf_authorization",
    id: "doc-pf-authorization",
    name: "PF Authorization.pdf",
  },
  { apiKey: "formf", id: "doc-formf", name: "Form F.pdf" },
];

const mapDocumentsFromDetail = (detail = {}) => {
  const personal = detail.personal || {};
  const aadhaar = personal.aadhaar || {};
  const bank = detail.bank || {};
  const education = Array.isArray(detail.education) ? detail.education : [];
  const employment = detail.employment || {};
  const insurance = detail.insurance || {};
  const declaration = detail.declaration || {};

  const personalDocs = [
    buildDocFromS3("doc-aadhaar", "Aadhaar.pdf", aadhaar.s3_key),
    buildDocFromS3(
      "doc-aadhaar-photo",
      "Aadhaar Photo.jpg",
      aadhaar.photo_s3_key,
    ),
    buildDocFromS3(
      "doc-profile-photo",
      "Profile Photo.jpg",
      personal.profile_photo_s3_key,
    ),
  ].filter(Boolean);

  const educationDocs = education.flatMap((item, index) => {
    const qualification =
      item.course_type || item.qualification || item.level || "";
    const labels = getEducationDocumentLabels(qualification);

    return [
      buildDocFromS3(
        `doc-edu-marksheet-${index}`,
        labels.marksheet,
        item.marksheets_s3_key,
      ),
      buildDocFromS3(
        `doc-edu-certificate-${index}`,
        labels.certificate,
        item.certificate_s3_key,
      ),
    ].filter(Boolean);
  });

  const employmentLetterDocs = [
    buildDocFromS3(
      "doc-resignation",
      "Resignation Letter.pdf",
      employment.resignation_letter_s3_key,
    ),
    buildDocFromS3(
      "doc-relieving",
      "Relieving Letter.pdf",
      employment.relieving_letter_s3_key,
    ),
    buildDocFromS3(
      "doc-epfo",
      "EPFO Passbook.pdf",
      employment.epfo_passbook_s3_key,
    ),
  ].filter(Boolean);

  const employmentFormDocs = EMPLOYMENT_FORM_DOC_DEFS.map((def) =>
    buildEmploymentFormDoc(def.id, def.name, employment[def.apiKey]),
  ).filter(Boolean);

  const employmentDocs = [...employmentLetterDocs, ...employmentFormDocs];

  const bankDocs = [
    buildDocFromS3("doc-pan", "PAN Card.jpg", bank.pancard_s3_key),
    buildDocFromS3("doc-bank", "Bank Document.pdf", bank.bankdoc_s3_key),
  ].filter(Boolean);

  const insuranceDocs = [
    buildDocFromS3(
      "doc-nischint",
      "Nischint Forms.pdf",
      insurance.nischint_s3_key,
    ),
  ].filter(Boolean);

  const declarationDocs = [
    buildDocFromS3(
      "doc-bgv",
      "BGV Authorization.pdf",
      declaration.bgv_authorization_s3_key,
    ),
    buildDocFromS3(
      "doc-emp-declaration",
      "Employee Declaration.pdf",
      declaration.employee_declaration_s3_key,
    ),
  ].filter(Boolean);

  return {
    personal: personalDocs,
    address: [],
    education: educationDocs,
    employment: employmentDocs,
    bank: bankDocs,
    insurance: insuranceDocs,
    declarations: declarationDocs,
    jobDetails: [],
  };
};

/**
 * Normalize get_candidate_approval_details into Review Request UI shape.
 * Reads candidate payload from response.body.data.
 */
export const normalizeApprovalDetails = (response, requestId) => {
  const detail = getApprovalDetailsPayload(response);

  const personal = mapPersonalSection(detail.personal || {});
  const address = mapAddressSection(detail.address || {});
  const education = mapEducationSection(detail.education || []);
  const employment = mapEmploymentSection(detail.employment || {});
  const bank = mapBankSection(detail.bank || {});
  const insurance = mapInsuranceSection(detail.insurance || {});
  const declarations = mapDeclarationsSection(
    detail.declaration || detail.declarations || {},
  );
  const jobDetails = mapJobDetailsSection(
    detail.job_details || detail.jobDetails || {},
  );

  const candidateName =
    detail.candidate_name ||
    detail.candidateName ||
    personal.full_name ||
    [personal.first_name, personal.middle_name, personal.last_name]
      .filter(Boolean)
      .join(" ") ||
    "";

  return {
    id:
      detail.id != null
        ? String(detail.id)
        : detail.request_id ||
          detail.requestId ||
          requestId ||
          "",
    candidate_name: candidateName,
    designation:
      detail.designation ||
      jobDetails.designation ||
      detail.role ||
      detail.job_title ||
      "-",
    store_code: detail.store_code || detail.storeCode || "-",
    store_name: detail.store_name || detail.storeName || "",
    status: normalizeOnboardingStatus(detail.status),
    approval_status:
      detail.approval_status == null ||
      String(detail.approval_status).trim() === ""
        ? ""
        : String(detail.approval_status).trim(),
    onboarding_id: normalizeOnboardingId(
      detail.id ?? detail.onboarding_id ?? detail.onboardingId ?? null,
    ),
    employee_id:
      detail.employee_id ||
      detail.employeeId ||
      jobDetails.employee_id ||
      null,
    form_completion_pct: detail.form_completion_pct,
    section_completion: detail.section_completion || {},
    sections: {
      personal,
      address,
      education,
      employment,
      bank,
      insurance,
      declarations,
      jobDetails,
    },
    documents: detail.documents || mapDocumentsFromDetail(detail),
    updatedFields:
      detail.updated_fields ||
      detail.updatedFields ||
      detail.updated_fields_list ||
      [],
    approval_feedback:
      detail.approval_feedback &&
      typeof detail.approval_feedback === "object" &&
      !Array.isArray(detail.approval_feedback)
        ? detail.approval_feedback
        : {},
    hrEditedFields: normalizeHrEditedFieldIndex(detail),
    raw: detail,
  };
};

/**
 * Normalize get_job_details_dropdown options from response.body.data.
 * Prefer API arrays when present; fall back to local mocks only when missing/empty.
 */
export const normalizeJobDetailsDropdown = (response) => {
  if (!response) return { ...JOB_DETAILS_OPTIONS, locTypeList: [] };

  const body = getResponseBody(response);
  const data =
    body?.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : body?.dropdowns && typeof body.dropdowns === "object"
        ? body.dropdowns
        : body?.options && typeof body.options === "object"
          ? body.options
          : body && typeof body === "object" && !Array.isArray(body)
            ? body
            : {};

  const toStringOptions = (list) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return list
      .map((item) => {
        if (item == null) return "";
        if (typeof item !== "object") return String(item);
        return String(
          item.label ??
            item.name ??
            item.display_name ??
            item.key ??
            item.value ??
            "",
        );
      })
      .filter((item) => item !== "");
  };

  /** Object options for CustomDropdown isObjectDrop fields */
  const toObjectOptions = (list) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.map((item) => {
      if (item && typeof item === "object") {
        if ("key" in item && "value" in item) return item;
        if ("label" in item && "value" in item) {
          return { key: item.label, value: item.value };
        }
        const label = String(
          item.label ?? item.name ?? item.key ?? item.value ?? "",
        );
        return { key: label, value: item.value ?? label };
      }
      const label = String(item);
      return { key: label, value: item };
    });
  };

  const pickStrings = (keys, fallback) => {
    for (const key of keys) {
      const normalized = toStringOptions(data[key]);
      if (normalized?.length) return normalized;
    }
    return fallback;
  };

  const pickObjects = (keys, fallback) => {
    for (const key of keys) {
      const normalized = toObjectOptions(data[key]);
      if (normalized?.length) return normalized;
    }
    return fallback;
  };

  return {
    // Keep mock defaults for keys the API does not return
    ...JOB_DETAILS_OPTIONS,

    // Mapped Job Details dropdowns from API body.data
    confirmationStatus: pickStrings(
      ["confirmationStatus", "confirmation_status"],
      JOB_DETAILS_OPTIONS.confirmationStatus,
    ),
    employeeType: pickStrings(
      ["employeeType", "employee_type"],
      JOB_DETAILS_OPTIONS.employeeType,
    ),
    employeeSubType: pickStrings(
      ["employeeSubType", "employee_sub_type"],
      JOB_DETAILS_OPTIONS.employeeSubType,
    ),
    employmentType: pickStrings(
      ["employmentType", "employment_type"],
      JOB_DETAILS_OPTIONS.employmentType,
    ),
    jobBand: pickStrings(
      ["jobBand", "band", "job_band"],
      JOB_DETAILS_OPTIONS.jobBand,
    ),
    jobGrade: pickStrings(
      ["jobGrade", "grade", "job_grade"],
      JOB_DETAILS_OPTIONS.jobGrade,
    ),
    designationNameAndCode: pickStrings(
      ["designationNameAndCode", "designation", "designation_name_and_code"],
      JOB_DETAILS_OPTIONS.designationNameAndCode,
    ),
    jobLevelAndCode: pickStrings(
      ["jobLevelAndCode", "job_level", "job_level_and_code"],
      JOB_DETAILS_OPTIONS.jobLevelAndCode,
    ),
    businessUnit: pickStrings(
      ["businessUnit", "business_unit"],
      JOB_DETAILS_OPTIONS.businessUnit,
    ),
    businessUnitHeadName: pickStrings(
      ["businessUnitHeadName", "business_unit_head"],
      JOB_DETAILS_OPTIONS.businessUnitHeadName,
    ),
    departmentCode: pickStrings(
      ["departmentCode", "department_code"],
      JOB_DETAILS_OPTIONS.departmentCode,
    ),
    departmentHierarchy: pickStrings(
      ["departmentHierarchy", "departments_hierarchy"],
      JOB_DETAILS_OPTIONS.departmentHierarchy,
    ),
    groupCompanyCode: pickStrings(
      ["groupCompanyCode", "group_company_code"],
      JOB_DETAILS_OPTIONS.groupCompanyCode,
    ),
    baseOfficeLocation: pickStrings(
      ["baseOfficeLocation", "base_office_location"],
      JOB_DETAILS_OPTIONS.baseOfficeLocation,
    ),
    // Location Type — use loc_name from get_job_details_dropdown (no mock fallback)
    locTypeList: pickStrings(["loc_name"], []),
    officeCountry: pickStrings(
      ["officeCountry", "office_country"],
      JOB_DETAILS_OPTIONS.officeCountry,
    ),
    officeState: pickStrings(
      ["officeState", "office_state"],
      JOB_DETAILS_OPTIONS.officeState,
    ),
    officeCity: pickStrings(
      ["officeCity", "office_city"],
      JOB_DETAILS_OPTIONS.officeCity,
    ),
    costCenter: pickStrings(
      ["costCenter", "cost_center"],
      JOB_DETAILS_OPTIONS.costCenter,
    ),
    // Not returned by current API — keep existing mock options
    workAreaCode: pickStrings(
      ["workAreaCode", "work_area_code_x", "work_area_code"],
      JOB_DETAILS_OPTIONS.workAreaCode,
    ),
    timeZone: pickStrings(
      ["timeZone", "time_zone"],
      JOB_DETAILS_OPTIONS.timeZone,
    ),
    attendanceShift: pickStrings(
      ["attendanceShift", "attendance_shift"],
      JOB_DETAILS_OPTIONS.attendanceShift,
    ),
    attendancePolicy: pickStrings(
      ["attendancePolicy", "attendance_policy"],
      JOB_DETAILS_OPTIONS.attendancePolicy,
    ),
    attendanceWeeklyOff: pickStrings(
      ["attendanceWeeklyOff", "attendance_weeklyoff"],
      JOB_DETAILS_OPTIONS.attendanceWeeklyOff,
    ),
    vendorName: pickStrings(
      ["vendorName", "vendor_name"],
      JOB_DETAILS_OPTIONS.vendorName,
    ),
    // API returns ["Yes","No"] — convert to object options for existing isObjectDrop UI
    billAmazon: pickObjects(
      ["billAmazon", "bill_amazon"],
      JOB_DETAILS_OPTIONS.billAmazon,
    ),
    // Not returned by current API — keep existing mock options
    channel: pickObjects(["channel"], JOB_DETAILS_OPTIONS.channel),

    // Extra API arrays (no clear Job Details dropdown mapping — exposed for reuse)
    location_fin: toStringOptions(data.location_fin) || [],
    loc_name: toStringOptions(data.loc_name) || [],
    employeeStatus: toStringOptions(data.employeeStatus) || [],
    genders: toStringOptions(data.genders) || [],
    maritalStatus: toStringOptions(data.maritalStatus) || [],
    nationality: toStringOptions(data.nationality) || [],
  };
};

/** Job Details keys returned by copy_employee_details */
const COPY_EMPLOYEE_JOB_DETAIL_KEYS = [
  // `employee_id` is intentionally excluded — similar-employee ID is only
  // used to fetch details and must never replace the candidate's Employee ID.
  "date_of_joining",
  "date_of_confirmation",
  "confirmation_status",
  "employee_type",
  "employee_sub_type",
  "employment_type",
  "band",
  "grade",
  "division",
  "designation",
  "position_id",
  "job_level",
  "job_level_code",
  "business_unit",
  "business_unit_head",
  "department_code",
  "departments_hierarchy",
  "group_company",
  "group_company_code",
  "base_office_location",
  "location_type",
  "office_country",
  "office_state",
  "office_region",
  "office_city",
  "city_type",
  "cost_center",
  "centre_type",
  "work_area_code_x",
  "direct_manager_employee_id",
  "direct_manager_name",
  "direct_manager_email",
  "hrbp_employee_id",
  "hrbp_name",
  "hrbp_email_id",
  "time_zone",
  "attendance_shift",
  "attendance_policy",
  "attendance_weeklyoff",
  "contribution_level",
  "functional_area",
  "functional_area_cost_center",
  "functional_area_code",
  "vendor_name",
  "vendor_emp_id",
  "bill_amazon",
  "workforce_engagement",
  "channel",
];

/**
 * Normalize copy_employee_details into a Job Details form patch.
 * Reads employee payload from response.body.data.
 * - Includes only known keys present on the payload
 * - null / empty → "" (clear field)
 * - Missing keys are omitted so existing form values are preserved
 */
export const normalizeCopiedEmployeeDetails = (response) => {
  const body = getResponseBody(response);
  const data =
    body?.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : body?.job_details && typeof body.job_details === "object"
        ? body.job_details
        : body?.jobDetails && typeof body.jobDetails === "object"
          ? body.jobDetails
          : body?.employee_details && typeof body.employee_details === "object"
            ? body.employee_details
            : body?.details && typeof body.details === "object"
              ? body.details
              : body && typeof body === "object" && !Array.isArray(body)
                ? body
                : {};

  // Ignore envelope-only objects that have no employee fields
  if (
    data.status != null &&
    data.data == null &&
    data.employee_id == null &&
    data.date_of_joining == null &&
    data.designation == null
  ) {
    return {};
  }

  const patch = {};

  COPY_EMPLOYEE_JOB_DETAIL_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(data, key)) return;
    const value = data[key];
    if (value == null) {
      patch[key] = "";
      return;
    }
    if (typeof value === "string") {
      patch[key] = value;
      return;
    }
    // Keep numbers/booleans as-is for form binding (stringify for inputs)
    patch[key] = value;
  });

  return patch;
};

/**
 * Merge a copy-employee patch into current Job Details without dropping misc.
 * Always preserves the candidate's employee_id (never overwrite from copy).
 */
export const mergeCopiedJobDetailsIntoForm = (
  currentJobDetails = {},
  copiedPatch = {},
) => {
  const current =
    currentJobDetails && typeof currentJobDetails === "object"
      ? currentJobDetails
      : {};
  const patch =
    copiedPatch && typeof copiedPatch === "object" ? copiedPatch : {};

  const { employee_id: _ignoredCopiedEmployeeId, ...safePatch } = patch;

  return {
    ...current,
    ...safePatch,
    // Candidate Employee ID must remain unchanged after copy
    employee_id: current.employee_id,
    misc:
      current.misc && typeof current.misc === "object" && !Array.isArray(current.misc)
        ? current.misc
        : {},
  };
};

/**
 * Job Details form keys rendered in Review Request (excludes backend-only
 * required keys like employee_status that are not on this screen).
 */
const REVIEW_JOB_DETAILS_FORM_KEYS = new Set([
  "employee_id",
  ...COPY_EMPLOYEE_JOB_DETAIL_KEYS,
  "source_of_job_application",
  "previousEmpCode",
  "previousEmailID",
  "more_user_details",
  "previous_worked_for_more",
  "family_in_more",
]);

/**
 * Job Details fields that must be non-empty for Create ID, but should not
 * use format/regex validation (Review Request editable overrides).
 */
const CREATE_ID_REQUIRED_ONLY_KEYS = new Set([
  "position_id",
  "direct_manager_name",
  "direct_manager_email",
  "hrbp_name",
  "hrbp_email_id",
]);

export const DATE_OF_JOINING_MIN_PAST_DAYS = 7;
export const DATE_OF_JOINING_MAX_FUTURE_DAYS = 90;

export const getDateOfJoiningMinDate = () =>
  moment().subtract(DATE_OF_JOINING_MIN_PAST_DAYS, "days").startOf("day");

export const getDateOfJoiningMaxDate = () =>
  moment().add(DATE_OF_JOINING_MAX_FUTURE_DAYS, "days").startOf("day");

export const isDateOfJoiningInAllowedRange = (value) => {
  if (value == null || value === "") return true;
  const selected = moment(value);
  if (!selected.isValid()) return false;
  const day = selected.clone().startOf("day");
  return (
    !day.isBefore(getDateOfJoiningMinDate()) &&
    !day.isAfter(getDateOfJoiningMaxDate())
  );
};

/** True when a Date of Joining is present and outside the allowed window. */
export const hasInvalidDateOfJoiningValue = (jobDetails = {}) => {
  const raw = jobDetails?.date_of_joining;
  const value = raw == null ? "" : typeof raw === "string" ? raw.trim() : raw;
  if (value == null || value === "") return false;
  return !isDateOfJoiningInAllowedRange(value);
};

/**
 * Validate Job Details against commonFields (same rules as Add Employee).
 * Returns an errors map keyed by field name.
 */
export const validateJobDetailsForCreateId = (
  jobDetails = {},
  userDetailsFields = {},
) => {
  const errors = {};
  const misc =
    jobDetails?.misc && typeof jobDetails.misc === "object"
      ? jobDetails.misc
      : {};
  const values = { ...jobDetails, ...misc };

  Object.entries(userDetailsFields || {}).forEach(([key, config]) => {
    if (!config || config.section !== "job details") return;
    if (!REVIEW_JOB_DETAILS_FORM_KEYS.has(key)) return;

    const raw = values[key];
    const value =
      raw == null ? "" : typeof raw === "string" ? raw.trim() : raw;

    if (
      key !== "employee_id" &&
      config.required &&
      (value == null || value === "")
    ) {
      errors[key] = {
        message: `${key} is required`,
        section: config.section,
      };
      return;
    }

    // These Review Request fields are editable with required-only checks
    if (CREATE_ID_REQUIRED_ONLY_KEYS.has(key)) return;

    if (
      config.regex &&
      value != null &&
      value !== "" &&
      typeof value === "string" &&
      !config.regex.test(value)
    ) {
      errors[key] = {
        message: `${key} is invalid`,
        section: config.section,
      };
      return;
    }

    if (
      key === "date_of_joining" &&
      value &&
      !isDateOfJoiningInAllowedRange(value)
    ) {
      errors[key] = {
        message: `${key} is invalid`,
        section: config.section,
      };
    }
  });

  return errors;
};

/**
 * Map Jedi_HR UI / document flag keys → canonical approval_feedback.field_name list.
 * Combined fields (address lines, full names) expand to multiple Onboarding keys.
 */
const resolveFeedbackFieldNames = (fieldKey = "") =>
  resolveCanonicalFieldNames(fieldKey);

const parseEducationDocRecordIndex = (fieldKey = "") => {
  const match = String(fieldKey || "").match(
    /^doc-edu-(?:marksheet|certificate)-(\d+)$/,
  );
  if (!match) return null;
  return Number(match[1]);
};

const parseEducationDocKind = (fieldKey = "") => {
  const match = String(fieldKey || "").match(
    /^doc-edu-(marksheet|certificate)(?:-\d+)?$/,
  );
  return match ? match[1] : "";
};

/**
 * Resolve education document UI keys to Onboarding canonical field_name values
 * using the education record's qualification at record_index.
 */
const resolveEducationDocumentFieldNames = (
  fieldKey = "",
  educationRecords = [],
) => {
  const docKind = parseEducationDocKind(fieldKey);
  if (!docKind) return [];

  const recordIndex = parseEducationDocRecordIndex(fieldKey);
  const qualification =
    recordIndex != null
      ? educationRecords?.[recordIndex]?.qualification ||
        educationRecords?.[recordIndex]?.course_type ||
        educationRecords?.[recordIndex]?.level ||
        ""
      : "";

  const fieldName = getEducationDocumentCanonicalFieldName(
    docKind,
    qualification,
  );
  return fieldName ? [fieldName] : [];
};

/** Invert Jedi UI → canonical map (canonical → preferred Jedi UI key) */
const buildCanonicalToJediUiMap = () => {
  const reverse = {};

  Object.entries(JEDI_UI_TO_CANONICAL_FIELD_NAMES).forEach(
    ([uiKey, apiNames]) => {
      const names = Array.isArray(apiNames) ? apiNames : [apiNames];
      names.forEach((apiName) => {
        if (!apiName) return;
        const existing = reverse[apiName];
        if (existing) {
          if (!String(existing).startsWith("doc-")) return;
          if (String(uiKey).startsWith("doc-")) return;
        }
        reverse[apiName] = uiKey;
      });
    },
  );

  Object.entries(CANONICAL_FIELD_NAME_ALIASES_TO_JEDI_UI).forEach(
    ([apiName, uiKey]) => {
      if (apiName && uiKey) reverse[apiName] = uiKey;
    },
  );

  return reverse;
};

const CANONICAL_TO_JEDI_UI_FIELD_KEY = buildCanonicalToJediUiMap();

/**
 * Resolve backend approval_feedback.field_name → Jedi_HR UI field / document key.
 * Section-aware for keys that repeat across sections (e.g. authorization).
 * Multiple Onboarding keys for a combined Jedi field all resolve to that Jedi field.
 */
export const resolveUiFieldKeyFromFeedbackName = (
  apiFieldName = "",
  feedbackSection = "",
) => {
  const key = String(apiFieldName || "").trim();
  if (!key) return "";

  const section = String(feedbackSection || "")
    .trim()
    .toLowerCase();

  if (key === "authorization") {
    if (section === "employment") return "doc-pf-authorization";
    if (section === "insurance") return "doc-nischint";
  }

  if (key === "marksheet" || key === "consolidated_marksheet") {
    return "doc-edu-marksheet";
  }
  if (key === "certificate" || key === "graduation_certificate") {
    return "doc-edu-certificate";
  }

  return CANONICAL_TO_JEDI_UI_FIELD_KEY[key] || key;
};

/** Invert UI section → feedback section map for loading saved remarks */
const FEEDBACK_SECTION_TO_UI_SECTIONS = Object.entries(
  UI_SECTION_TO_FEEDBACK_KEY,
).reduce((acc, [uiSection, feedbackSection]) => {
  if (!acc[feedbackSection]) acc[feedbackSection] = [];
  acc[feedbackSection].push(uiSection);
  return acc;
}, {});

/**
 * Build fieldFlags from backend approval_feedback so saved draft remarks
 * reappear on the matching UI fields/documents.
 *
 * @param {object} approvalFeedback - section → [{ field_name, field_remark }]
 * @param {object} options
 * @param {(sectionKey: string, fieldKey: string) => object|null} options.findField
 * @param {(field: object) => boolean} options.isFlagBlocked
 */
export const buildFlagsFromApprovalFeedback = (
  approvalFeedback = {},
  options = {},
) => {
  const flags = {};
  const now = new Date().toISOString();
  const findField =
    typeof options.findField === "function" ? options.findField : () => null;
  const isFlagBlocked =
    typeof options.isFlagBlocked === "function"
      ? options.isFlagBlocked
      : () => false;
  const getFlagKeyFn =
    typeof options.getFlagKey === "function"
      ? options.getFlagKey
      : (sectionKey, fieldKey, recordIndex = null) =>
          recordIndex == null
            ? `${sectionKey}-${fieldKey}`
            : `${sectionKey}-${recordIndex}-${fieldKey}`;

  if (
    !approvalFeedback ||
    typeof approvalFeedback !== "object" ||
    Array.isArray(approvalFeedback)
  ) {
    return flags;
  }

  Object.entries(approvalFeedback).forEach(([feedbackSection, items]) => {
    if (!Array.isArray(items) || items.length === 0) return;

    const uiSections =
      FEEDBACK_SECTION_TO_UI_SECTIONS[feedbackSection] ||
      FEEDBACK_SECTION_TO_UI_SECTIONS[String(feedbackSection).toLowerCase()] ||
      [feedbackSection];

    items.forEach((item) => {
      const remark = String(
        item?.field_remark ?? item?.remark ?? "",
      ).trim();
      const apiFieldName = String(item?.field_name ?? "").trim();
      if (!remark || !apiFieldName) return;

      let fieldKey = resolveUiFieldKeyFromFeedbackName(
        apiFieldName,
        feedbackSection,
      );
      if (!fieldKey) return;

      const recordIndex =
        item?.record_index ?? item?.recordIndex ?? null;

      // Education docs are stored as doc-edu-marksheet-0 / doc-edu-certificate-1
      if (
        (fieldKey === "doc-edu-marksheet" ||
          fieldKey === "doc-edu-certificate") &&
        recordIndex != null &&
        recordIndex !== ""
      ) {
        fieldKey = `${fieldKey}-${recordIndex}`;
      }

      let sectionKey = uiSections[0];
      let field = null;

      for (const candidateSection of uiSections) {
        const matched = findField(candidateSection, fieldKey);
        if (matched) {
          sectionKey = candidateSection;
          field = matched;
          break;
        }
      }

      if (field && isFlagBlocked(field)) return;

      // Document flags use sectionKey-fieldKey (no recordIndex in the key)
      const isEducationDoc = /^doc-edu-(marksheet|certificate)-\d+$/.test(
        fieldKey,
      );
      const flagKey = isEducationDoc
        ? getFlagKeyFn(sectionKey, fieldKey, null)
        : getFlagKeyFn(sectionKey, fieldKey, recordIndex);

      // Multiple Onboarding keys (e.g. address house+street) collapse to one Jedi flag
      if (flags[flagKey]) return;

      flags[flagKey] = {
        remark,
        sectionKey,
        fieldKey,
        recordIndex: isEducationDoc ? null : recordIndex,
        fieldLabel: field?.label || item?.field_label || fieldKey,
        flaggedAt: item?.flagged_at || item?.flaggedAt || now,
        updatedAt: item?.updated_at || item?.updatedAt || now,
      };
    });
  });

  return flags;
};

/**
 * Build updated-field entries from approval_feedback for the
 * "Updated Fields Only" filter (Re-submitted requests).
 *
 * @returns {Array<{ sectionKey, fieldKey, recordIndex, remark? }>}
 */
export const buildUpdatedFieldsFromApprovalFeedback = (
  approvalFeedback = {},
) => {
  const updatedFields = [];

  if (
    !approvalFeedback ||
    typeof approvalFeedback !== "object" ||
    Array.isArray(approvalFeedback)
  ) {
    return updatedFields;
  }

  Object.entries(approvalFeedback).forEach(([feedbackSection, items]) => {
    if (!Array.isArray(items) || items.length === 0) return;

    const uiSections =
      FEEDBACK_SECTION_TO_UI_SECTIONS[feedbackSection] ||
      FEEDBACK_SECTION_TO_UI_SECTIONS[String(feedbackSection).toLowerCase()] ||
      [feedbackSection];

    items.forEach((item) => {
      const apiFieldName = String(item?.field_name ?? "").trim();
      if (!apiFieldName) return;

      let fieldKey = resolveUiFieldKeyFromFeedbackName(
        apiFieldName,
        feedbackSection,
      );
      if (!fieldKey) return;

      const recordIndex =
        item?.record_index ?? item?.recordIndex ?? null;

      if (
        (fieldKey === "doc-edu-marksheet" ||
          fieldKey === "doc-edu-certificate") &&
        recordIndex != null &&
        recordIndex !== ""
      ) {
        fieldKey = `${fieldKey}-${recordIndex}`;
      }

      const sectionKey = uiSections[0];
      const isEducationDoc = /^doc-edu-(marksheet|certificate)-\d+$/.test(
        fieldKey,
      );

      updatedFields.push({
        sectionKey,
        fieldKey,
        recordIndex: isEducationDoc ? null : recordIndex,
        remark: String(item?.field_remark ?? item?.remark ?? "").trim(),
      });
    });
  });

  return updatedFields;
};

/**
 * Build approval_feedback from current field/document flags.
 * Backend contract: { section: [{ field_name, field_remark, record_index? }] }
 * Combined Jedi fields expand to multiple canonical Onboarding keys.
 * Education docs map field_name from the record's qualification.
 */
export const buildApprovalFeedback = (
  fieldFlags = {},
  sectionKeys = FEEDBACK_SECTION_KEYS,
  options = {},
) => {
  const feedback = emptyApprovalFeedback(sectionKeys);
  const educationRecords = Array.isArray(options.educationRecords)
    ? options.educationRecords
    : [];

  Object.values(fieldFlags || {}).forEach((flag) => {
    if (!flag) return;
    const remark = String(flag.remark || flag.field_remark || "").trim();
    if (!remark) return;

    const feedbackKey =
      UI_SECTION_TO_FEEDBACK_KEY[flag.sectionKey] || flag.sectionKey;
    if (!Object.prototype.hasOwnProperty.call(feedback, feedbackKey)) {
      return;
    }

    const uiFieldKey = flag.fieldKey || flag.field_name;
    const eduFieldNames = resolveEducationDocumentFieldNames(
      uiFieldKey,
      educationRecords,
    );
    const fieldNames = eduFieldNames.length
      ? eduFieldNames
      : resolveFeedbackFieldNames(uiFieldKey);
    if (!fieldNames.length) return;

    const eduIndex = parseEducationDocRecordIndex(uiFieldKey);
    const recordIndex =
      eduIndex != null
        ? eduIndex
        : flag.recordIndex != null
          ? flag.recordIndex
          : flag.record_index != null
            ? flag.record_index
            : null;

    fieldNames.forEach((fieldName) => {
      if (!fieldName) return;
      const entry = {
        field_name: fieldName,
        field_remark: remark,
      };
      if (recordIndex != null && recordIndex !== "") {
        entry.record_index = recordIndex;
      }
      feedback[feedbackKey].push(entry);
    });
  });

  return feedback;
};

/**
 * Resolve domain_id (email local-part) from auth state.
 */
export const resolveDomainId = (authState = {}) => {
  const userDetails =
    authState?.userDetailsResponse?.user_details ||
    authState?.userDetailsResponse?.body?.user_details ||
    authState?.userDetailsResponse?.body ||
    {};

  const emailCandidates = [
    userDetails?.company_email_id,
    userDetails?.personal_email_id,
    authState?.loginResponse?.email,
    authState?.loginResponse?.company_email_id,
    authState?.loginResponse?.body?.email,
    authState?.loginResponse?.body?.company_email_id,
  ];

  for (const email of emailCandidates) {
    if (typeof email === "string" && email.includes("@")) {
      return email.split("@")[0];
    }
  }

  return (
    authState?.loginResponse?.username ||
    authState?.loginResponse?.user_name ||
    authState?.employeeID ||
    ""
  );
};

const toNullableValue = (value) => {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return value;
};

const joinNameParts = (...parts) =>
  parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");

const buildLineAddress = (house, street) => {
  const line = {
    house: toNullableValue(house) ?? "",
    street: toNullableValue(street) ?? "",
  };
  if (!line.house && !line.street) {
    return { house: "", street: "" };
  }
  return line;
};

const buildLine2Address = (area, landmark) => {
  const line = {
    area: toNullableValue(area) ?? "",
    landmark: toNullableValue(landmark) ?? "",
  };
  if (!line.area && !line.landmark) {
    return { area: "", landmark: "" };
  }
  return line;
};

const mapEducationRecordsForSave = (education = {}, rawEducation) => {
  if (Array.isArray(rawEducation) && rawEducation.length > 0) {
    return rawEducation.map((item) => ({ ...item }));
  }

  const records = Array.isArray(education?.records) ? education.records : [];
  return records.map((item) => {
    const cgpa = String(item.cgpa_percentage || "").trim();
    const explicitMax = String(item.maximum_cgpa_percentage ?? "").trim();
    const [splitGpa, splitMax] = cgpa.includes("/")
      ? cgpa.split("/")
      : [cgpa, ""];
    const gpaPart = splitGpa;
    const maxPart = explicitMax || splitMax;
    const gpa = gpaPart != null && gpaPart !== "" ? Number(gpaPart) : null;
    const maxGpa = maxPart != null && maxPart !== "" ? Number(maxPart) : null;

    return {
      gpa: Number.isFinite(gpa) ? gpa : toNullableValue(item.gpa),
      max_gpa: Number.isFinite(maxGpa) ? maxGpa : toNullableValue(item.max_gpa),
      end_date: toNullableValue(item.graduation_date || item.end_date),
      start_date: toNullableValue(item.start_date),
      course_type: toNullableValue(
        String(item.qualification || item.course_type || "").toUpperCase(),
      ),
      institute_name: toNullableValue(item.college || item.institute_name),
      university_name: toNullableValue(item.university || item.university_name),
      education_degree: toNullableValue(item.degree || item.education_degree),
      marksheets_s3_key: toNullableValue(item.marksheets_s3_key) ?? "",
      certificate_s3_key: toNullableValue(item.certificate_s3_key) ?? "",
      highest_qualification: !!item.highest_qualification,
    };
  });
};

const mapJobHistoryForSave = (employment = {}, rawEmployment) => {
  const rawHistory = rawEmployment?.job_history;
  if (Array.isArray(rawHistory) && rawHistory.length > 0) {
    return rawHistory.map((item) => ({ ...item }));
  }

  const records = Array.isArray(employment?.records) ? employment.records : [];
  return records.map((item) => {
    const isCurrent = !!(
      item.current_check ||
      item.is_last_experience ||
      item.is_current
    );
    const endDateRaw = item.last_date || item.end_date || "";
    const endDate =
      isCurrent || String(endDateRaw).trim().toLowerCase() === "current company"
        ? null
        : toNullableValue(endDateRaw);

    return {
      company_name: toNullableValue(item.company || item.company_name),
      job_title: toNullableValue(item.designation || item.job_title),
      designation: toNullableValue(item.designation || item.job_title),
      start_date: toNullableValue(item.start_date),
      end_date: endDate,
      current_check: isCurrent,
      is_current: isCurrent,
    };
  });
};

/**
 * Build job_details object for save_progress from current review form state.
 */
export const buildJobDetailsForSave = (formData = {}, rawDetail = {}) => {
  const job = formData?.jobDetails || {};
  const personal = formData?.personal || {};
  const address = formData?.address || {};
  const insurance = formData?.insurance || {};
  const education = formData?.education || {};
  const employment = formData?.employment || {};

  const rawPersonal = rawDetail?.personal || {};
  const rawAddress = rawDetail?.address || {};
  const rawCurrent =
    rawAddress.current_address || rawAddress.current || {};
  const rawPermanent =
    rawAddress.permanent_address || rawAddress.permanent || {};
  // Prefer current address for job_details address snapshot
  const sourceAddress =
    Object.keys(rawCurrent).length > 0 ? rawCurrent : rawPermanent;

  const misc =
    job.misc && typeof job.misc === "object" && !Array.isArray(job.misc)
      ? { ...job.misc }
      : {};

  const fathersName =
    joinNameParts(
      personal.father_first_name,
      personal.father_middle_name,
      personal.father_last_name,
    ) ||
    toNullableValue(rawPersonal.fathers_name);

  const line1FromRaw = buildLineAddress(
    sourceAddress.house,
    sourceAddress.street,
  );
  const line2FromRaw = buildLine2Address(
    sourceAddress.area,
    sourceAddress.landmark,
  );

  const hasRawLine1 = !!(line1FromRaw.house || line1FromRaw.street);
  const hasRawLine2 = !!(line2FromRaw.area || line2FromRaw.landmark);

  return {
    first_name: toNullableValue(personal.first_name || job.first_name),
    middle_name: toNullableValue(personal.middle_name || job.middle_name),
    last_name: toNullableValue(personal.last_name || job.last_name),
    date_of_joining: toNullableValue(job.date_of_joining),
    date_of_confirmation: toNullableValue(job.date_of_confirmation),
    confirmation_status: toNullableValue(job.confirmation_status),
    employee_type: toNullableValue(job.employee_type),
    employee_sub_type: toNullableValue(job.employee_sub_type),
    employment_type: toNullableValue(job.employment_type),
    band: toNullableValue(job.band),
    grade: toNullableValue(job.grade),
    division: toNullableValue(job.division),
    designation: toNullableValue(job.designation),
    position_id: toNullableValue(job.position_id),
    job_level: toNullableValue(job.job_level),
    job_level_code: toNullableValue(job.job_level_code || job.job_level),
    business_unit: toNullableValue(job.business_unit),
    business_unit_head: toNullableValue(job.business_unit_head),
    department_code: toNullableValue(job.department_code),
    departments_hierarchy: toNullableValue(job.departments_hierarchy),
    group_company: toNullableValue(job.group_company),
    group_company_code: toNullableValue(job.group_company_code),
    base_office_location: toNullableValue(job.base_office_location),
    location_type: toNullableValue(job.location_type),
    office_country: toNullableValue(job.office_country),
    office_state: toNullableValue(job.office_state),
    office_region: toNullableValue(job.office_region),
    office_city: toNullableValue(job.office_city),
    city_type: toNullableValue(job.city_type),
    cost_center: toNullableValue(job.cost_center),
    centre_type: toNullableValue(job.centre_type),
    work_area_code_x: toNullableValue(job.work_area_code_x),
    direct_manager_employee_id: toNullableValue(job.direct_manager_employee_id),
    direct_manager_name: toNullableValue(job.direct_manager_name),
    direct_manager_email: toNullableValue(job.direct_manager_email),
    hrbp_employee_id: toNullableValue(job.hrbp_employee_id),
    hrbp_name: toNullableValue(job.hrbp_name),
    hrbp_email_id: toNullableValue(job.hrbp_email_id),
    time_zone: toNullableValue(job.time_zone),
    attendance_shift: toNullableValue(job.attendance_shift),
    attendance_policy: toNullableValue(job.attendance_policy),
    attendance_weeklyoff: toNullableValue(job.attendance_weeklyoff),
    contribution_level: toNullableValue(job.contribution_level),
    functional_area: toNullableValue(job.functional_area),
    functional_area_cost_center: toNullableValue(
      job.functional_area_cost_center,
    ),
    functional_area_code: toNullableValue(job.functional_area_code),
    vendor_name: toNullableValue(job.vendor_name),
    vendor_emp_id: toNullableValue(job.vendor_emp_id),
    bill_amazon: toNullableValue(job.bill_amazon),
    workforce_engagement: toNullableValue(job.workforce_engagement),
    channel: toNullableValue(job.channel),
    blood_group: toNullableValue(
      insurance.blood_group || job.blood_group || rawDetail?.insurance?.blood_group,
    ),
    company_email_id: toNullableValue(job.company_email_id),
    personal_email_id: toNullableValue(
      personal.email_address || job.personal_email_id || rawPersonal.personal_email_id,
    ),
    marital_status: toNullableValue(
      personal.marital_status || job.marital_status || rawPersonal.marital_status,
    ),
    anniversary_date: toNullableValue(job.anniversary_date),
    gender: toNullableValue(personal.gender || job.gender || rawPersonal.gender),
    date_of_birth: toNullableValue(
      personal.date_of_birth || job.date_of_birth || rawPersonal.date_of_birth,
    ),
    personal_mobile_no: toNullableValue(
      personal.phone_number ||
        job.personal_mobile_no ||
        rawPersonal.personal_mobile_no,
    ),
    office_mobile_no: toNullableValue(
      personal.alternative_phone_number ||
        job.office_mobile_no ||
        rawPersonal.office_mobile_no,
    ),
    fathers_name: fathersName,
    nationality: toNullableValue(
      personal.nationality || job.nationality || rawPersonal.nationality,
    ),
    line_1_address: hasRawLine1
      ? line1FromRaw
      : buildLineAddress(
          address.current_address_house,
          address.current_address_street,
        ),
    line_2_address: hasRawLine2
      ? line2FromRaw
      : buildLine2Address(
          address.current_address_area,
          address.current_address_landmark,
        ),
    city: toNullableValue(
      address.current_address_city || sourceAddress.city || job.city,
    ),
    state: toNullableValue(
      address.current_address_state || sourceAddress.state || job.state,
    ),
    pincode: toNullableValue(
      address.current_address_pincode || sourceAddress.pincode || job.pincode,
    ),
    address_country: toNullableValue(
      sourceAddress.country || job.address_country || job.office_country,
    ),
    education: mapEducationRecordsForSave(education, rawDetail?.education),
    job_history: mapJobHistoryForSave(employment, rawDetail?.employment),
    misc,
  };
};

const hasEmployeeTypeSelected = (formData = {}, jobDetails = {}) => {
  const value =
    formData?.jobDetails?.employee_type ?? jobDetails?.employee_type ?? "";
  return String(value || "").trim() !== "";
};

/**
 * Build save_progress payload from current review state.
 * Matches backend contract keys exactly.
 */
/**
 * Read request version from get_candidate_approval_details body.data (or raw detail).
 * Returns a finite number, or null when the backend did not send version.
 */
export const extractRequestVersion = (source) => {
  if (source == null || typeof source !== "object") return null;

  const candidates = [];
  if (source.raw && typeof source.raw === "object") {
    candidates.push(source.raw);
  }
  candidates.push(source);
  const payload = getApprovalDetailsPayload(source);
  if (payload && typeof payload === "object") {
    candidates.push(payload);
  }
  if (source.body) {
    const body = getResponseBody(source);
    if (body && typeof body === "object") {
      candidates.push(body);
      if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
        candidates.push(body.data);
      }
    }
  }

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    const rawVersion =
      candidate?.version ??
      candidate?.request_version ??
      candidate?.requestVersion ??
      null;
    if (rawVersion == null || rawVersion === "") continue;
    const parsed = Number(rawVersion);
    // 0 is not a usable optimistic-lock version. Accepting it overwrites
    // the version from get_candidate_approval_details on the next save.
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return null;
};

/**
 * UI field key → save_progress.edits field_key.
 * Keys that already match the contract are left unchanged.
 */
const HR_EDIT_PAYLOAD_FIELD_KEYS = {
  permanent_address_house: "permanent_house",
  permanent_address_street: "permanent_street",
  permanent_address_area: "permanent_area",
  permanent_address_landmark: "permanent_landmark",
  permanent_address_city: "permanent_city",
  permanent_address_state: "permanent_state",
  permanent_address_pincode: "permanent_pincode",
  current_address_house: "current_house",
  current_address_street: "current_street",
  current_address_area: "current_area",
  current_address_landmark: "current_landmark",
  current_address_city: "current_city",
  current_address_state: "current_state",
  current_address_pincode: "current_pincode",
  nominee_full_name: "nominee_name",
  nominee_dob: "nominee_date_of_birth",
  emergency_full_name: "emergency_contact_name",
  emergency_relationship: "emergency_contact_relationship",
  emergency_phone: "emergency_contact_mobile",
  emergency_alternative_mobile: "emergency_alt_contact_mobile",
  emergency_email: "emergency_contact_email",
};

const HR_EDIT_RECORD_INDEX_SECTIONS = new Set(["education", "employment"]);

const resolveHrEditPayloadFieldKey = (fieldKey) =>
  HR_EDIT_PAYLOAD_FIELD_KEYS[fieldKey] || fieldKey;

const HR_EDITED_FIELD_SOURCE_KEYS = [
  "hr_edited_fields",
  "hrEditedFields",
  "edited_by_hr",
  "editedByHr",
  "hr_edited",
  "hrEdited",
];

const HR_EDITED_SECTION_KEYS = new Set([
  "personal",
  "address",
  "education",
  "employment",
  "bank",
  "insurance",
  "declaration",
  "declarations",
  "job_details",
  "jobDetails",
]);

const isHrEditedFieldList = (value) =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      typeof item === "string" ||
      (item &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        (item.field_key != null ||
          item.fieldKey != null ||
          item.field_name != null)),
  );

const isHrEditedSectionMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const entries = Object.entries(value);
  if (!entries.length) return false;
  return entries.every(
    ([section, fields]) =>
      HR_EDITED_SECTION_KEYS.has(section) && isHrEditedFieldList(fields),
  );
};

const readHrEditedFieldSource = (detail) => {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return null;

  for (let index = 0; index < HR_EDITED_FIELD_SOURCE_KEYS.length; index += 1) {
    const value = detail[HR_EDITED_FIELD_SOURCE_KEYS[index]];
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    if (isHrEditedSectionMap(value) || Object.keys(value).length === 0) {
      return value;
    }
  }

  const entries = Object.entries(detail);
  for (let index = 0; index < entries.length; index += 1) {
    const [key, value] = entries[index];
    if (HR_EDITED_SECTION_KEYS.has(key) || HR_EDITED_FIELD_SOURCE_KEYS.includes(key)) {
      continue;
    }
    if (isHrEditedSectionMap(value)) return value;
  }

  return null;
};

const hrEditedSectionAliases = (sectionKey) => {
  const key = String(sectionKey || "").trim();
  const aliases = [key];
  if (key === "declarations") aliases.push("declaration");
  if (key === "declaration") aliases.push("declarations");
  const feedbackKey = UI_SECTION_TO_FEEDBACK_KEY[key];
  if (feedbackKey && !aliases.includes(feedbackKey)) aliases.push(feedbackKey);
  return aliases;
};

const hrEditedRecordMatches = (listedIndex, recordIndex) => {
  if (listedIndex == null || listedIndex === "") return true;
  if (recordIndex == null || recordIndex === "") return false;
  return Number(listedIndex) === Number(recordIndex);
};

/**
 * Section → field keys already edited by HR, from get_candidate_approval_details.
 * Empty, null, or a missing list means no persisted tags.
 */
export const normalizeHrEditedFieldIndex = (detail) => {
  const source = readHrEditedFieldSource(detail);
  if (!source) return {};

  return Object.entries(source).reduce((index, [sectionKey, fields]) => {
    if (!isHrEditedFieldList(fields)) return index;
    const items = fields
      .map((item) => {
        if (typeof item === "string") {
          const fieldKey = item.trim();
          return fieldKey ? { fieldKey, recordIndex: null } : null;
        }
        const fieldKey = String(
          item?.field_key ?? item?.fieldKey ?? item?.field_name ?? "",
        ).trim();
        if (!fieldKey) return null;
        const rawIndex = item?.record_index ?? item?.recordIndex ?? null;
        return {
          fieldKey,
          recordIndex:
            rawIndex == null || rawIndex === "" ? null : rawIndex,
        };
      })
      .filter(Boolean);
    if (items.length) index[sectionKey] = items;
    return index;
  }, {});
};

/**
 * API field_key for this UI field when get_candidate_approval_details lists it.
 * Returns "" when the field was not persisted as an HR edit.
 */
export const listedHrEditFieldKey = (
  hrEditedFields,
  sectionKey,
  uiFieldKey,
  recordIndex = null,
) => {
  const uiKey = String(uiFieldKey || "").trim();
  if (!uiKey || !hrEditedFields || typeof hrEditedFields !== "object") return "";

  const payloadKey = resolveHrEditPayloadFieldKey(uiKey);
  const canonicalNames = resolveCanonicalFieldNames(uiKey);
  const sections = hrEditedSectionAliases(sectionKey);

  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
    const items = hrEditedFields[sections[sectionIndex]];
    if (!Array.isArray(items)) continue;
    for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
      const item = items[itemIndex];
      if (!hrEditedRecordMatches(item?.recordIndex, recordIndex)) continue;
      const apiKey = String(item?.fieldKey || "").trim();
      if (!apiKey) continue;
      if (apiKey === uiKey || apiKey === payloadKey) return apiKey;
      if (canonicalNames.includes(apiKey)) return apiKey;
      if (CANONICAL_FIELD_NAME_ALIASES_TO_JEDI_UI[apiKey] === uiKey) return apiKey;
    }
  }

  return "";
};

const hrEditCountSectionKey = (sectionKey) => {
  const key = String(sectionKey || "").trim();
  if (key === "declaration") return "declarations";
  if (key === "job_details") return "jobDetails";
  return key;
};

const hrEditSectionsMatch = (candidateSection, requestedSection) => {
  const requested = hrEditCountSectionKey(requestedSection);
  if (!requested) return true;
  const aliases = hrEditedSectionAliases(requested).map(hrEditCountSectionKey);
  return aliases.includes(hrEditCountSectionKey(candidateSection));
};

/**
 * One identity per edited field.
 * Personal, address, and insurance use section + field.
 * Education and employment also use record_index, so two records stay separate.
 * UI keys and API keys for the same field collapse to one identity.
 */
const hrEditedFieldCountIdentity = (sectionKey, fieldKey, recordIndex) => {
  const section = hrEditCountSectionKey(sectionKey);
  const field = resolveHrEditPayloadFieldKey(String(fieldKey || "").trim());
  if (!section || !field) return "";
  if (!HR_EDIT_RECORD_INDEX_SECTIONS.has(section)) {
    return `${section}|${field}`;
  }
  const hasIndex =
    recordIndex != null &&
    recordIndex !== "" &&
    Number.isFinite(Number(recordIndex));
  return hasIndex
    ? `${section}|${field}|${Number(recordIndex)}`
    : `${section}|${field}`;
};

/**
 * HR-edited fields for one Review Request section, from the normalized
 * get_candidate_approval_details list. Same field on two records stays two
 * entries. A repeated field_key + record_index is counted once.
 */
export const listHrEditedFieldsForSection = (hrEditedFields, sectionKey) => {
  if (!hrEditedFields || typeof hrEditedFields !== "object") return [];

  const seen = new Set();
  const items = [];
  hrEditedSectionAliases(sectionKey).forEach((key) => {
    const list = hrEditedFields[key];
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      const fieldKey = String(item?.fieldKey || "").trim();
      if (!fieldKey) return;
      const rawIndex = item?.recordIndex;
      const recordIndex =
        rawIndex == null || rawIndex === ""
          ? null
          : Number.isFinite(Number(rawIndex))
            ? Number(rawIndex)
            : rawIndex;
      const identity = hrEditedFieldCountIdentity(key, fieldKey, recordIndex);
      if (!identity || seen.has(identity)) return;
      seen.add(identity);
      items.push({ fieldKey, recordIndex });
    });
  });
  return items;
};

/**
 * Count persisted API edits and current-session edits together.
 * Pass sectionKey to count one section. Omit it to count every section.
 */
export const countHrEditedFields = ({
  hrEditedFields,
  history,
  sectionKey,
} = {}) => {
  const identities = new Set();
  const add = (section, fieldKey, recordIndex) => {
    if (sectionKey && !hrEditSectionsMatch(section, sectionKey)) return;
    const identity = hrEditedFieldCountIdentity(section, fieldKey, recordIndex);
    if (identity) identities.add(identity);
  };

  Object.entries(hrEditedFields || {}).forEach(([section, items]) => {
    if (!Array.isArray(items)) return;
    items.forEach((item) => {
      add(section, item?.fieldKey, item?.recordIndex);
    });
  });

  (Array.isArray(history) ? history : []).forEach((entry) => {
    add(entry?.sectionKey, entry?.fieldKey, entry?.recordIndex);
  });

  return identities.size;
};

export const hrEditedFieldsCountLabel = (count) => {
  const total = Number(count) || 0;
  return `${total} ${total === 1 ? "field" : "fields"} edited by HR`;
};

/**
 * Map confirmed HR Direct Edit pending entries to the save_progress.edits contract.
 * Every edit sends section_key, field_key, new_value, and reason.
 * record_index is included for education and employment only.
 */
export const buildHrDirectEditsForSave = (pendingEdits = []) => {
  if (!Array.isArray(pendingEdits) || pendingEdits.length === 0) return [];

  return pendingEdits
    .map((entry) => {
      const sectionKey = String(
        entry?.section_key ?? entry?.sectionKey ?? "",
      ).trim();
      const fieldKey = String(entry?.field_key ?? entry?.fieldKey ?? "").trim();
      const reason = String(entry?.reason ?? "").trim();
      if (!sectionKey || !fieldKey || !reason) return null;

      const newValue =
        entry?.new_value !== undefined
          ? entry.new_value
          : entry?.newValue !== undefined
            ? entry.newValue
            : "";

      const edit = {
        section_key: sectionKey,
        field_key: resolveHrEditPayloadFieldKey(fieldKey),
        new_value: newValue == null ? "" : newValue,
        reason,
      };

      if (HR_EDIT_RECORD_INDEX_SECTIONS.has(sectionKey)) {
        const rawIndex =
          entry?.record_index != null ? entry.record_index : entry?.recordIndex;
        if (rawIndex != null && rawIndex !== "") {
          const parsed = Number(rawIndex);
          edit.record_index = Number.isFinite(parsed) ? parsed : rawIndex;
        }
      }

      return edit;
    })
    .filter(Boolean);
};

export const buildSaveProgressPayload = ({
  onboardingId,
  formData = {},
  fieldFlags = {},
  domainId = "",
  employeeId = null,
  rawDetail = null,
  version = null,
  edits = [],
}) => {
  const raw =
    rawDetail ||
    formData?.raw ||
    {};
  const jobDetails = buildJobDetailsForSave(formData, raw);
  const resolvedEmployeeId = toNullableValue(
    employeeId ?? formData?.jobDetails?.employee_id ?? null,
  );
  const educationRecords = Array.isArray(formData?.education?.records)
    ? formData.education.records
    : [];
  const explicitVersion =
    version != null && version !== "" ? Number(version) : null;
  const rawVersion = extractRequestVersion(raw);
  const resolvedVersion =
    Number.isFinite(explicitVersion) && explicitVersion > 0
      ? explicitVersion
      : rawVersion;

  return {
    action: "save_progress",
    onboarding_id: onboardingId,
    ...(Number.isFinite(resolvedVersion) && resolvedVersion > 0
      ? { version: resolvedVersion }
      : {}),
    employee_id: resolvedEmployeeId,
    job_details_flag: hasEmployeeTypeSelected(formData, jobDetails),
    domain_id: domainId || "",
    edits: buildHrDirectEditsForSave(edits),
    approval_feedback: buildApprovalFeedback(fieldFlags, FEEDBACK_SECTION_KEYS, {
      educationRecords,
    }),
    job_details: jobDetails,
  };
};

/**
 * Build send_back_for_correction payload.
 * Matches backend contract: action, onboarding_id, approval_feedback
 * (no job_details section in feedback).
 */
export const buildSendBackPayload = ({
  onboardingId,
  fieldFlags = {},
  formData = null,
  educationRecords = null,
}) => {
  const records = Array.isArray(educationRecords)
    ? educationRecords
    : Array.isArray(formData?.education?.records)
      ? formData.education.records
      : [];

  return {
    action: "send_back_for_correction",
    onboarding_id: onboardingId,
    approval_feedback: buildApprovalFeedback(
      fieldFlags,
      SEND_BACK_FEEDBACK_SECTION_KEYS,
      { educationRecords: records },
    ),
  };
};

/**
 * Build mark_as_reviewed_and_create_id payload.
 */
export const buildCreateIdPayload = ({ onboardingId, employeeId }) => ({
  action: "mark_as_reviewed_and_create_id",
  onboarding_id: onboardingId,
  employee_id: String(employeeId || "").trim(),
});

/**
 * Read employee_id from save_progress response envelope.
 * Shape: { statusCode, body: { status, data: { employee_id } } }
 */
export const extractEmployeeIdFromSaveProgress = (response) => {
  const body = getResponseBody(response);
  const data =
    body?.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : body;

  const employeeId =
    data?.employee_id ??
    data?.employeeId ??
    body?.employee_id ??
    body?.employeeId ??
    null;

  return toNullableValue(employeeId);
};

/**
 * Read a positive version from save_progress body.data.version.
 * Does not invent, increment, or accept 0 as a replacement version.
 * A missing or invalid value returns null so the caller keeps the current version.
 */
export const extractVersionFromSaveProgress = (response) => {
  const body = getResponseBody(response);
  const data =
    body?.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : null;
  const rawVersion = data?.version;
  if (rawVersion == null || rawVersion === "") return null;
  const parsed = Number(rawVersion);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

/**
 * Read body.data from an onboarding action response (Approval Flow envelope).
 * Returns undefined when the data key is absent.
 */
export const extractOnboardingActionData = (response) => {
  const body = getResponseBody(response);
  if (!body || typeof body !== "object") return undefined;
  if (!Object.prototype.hasOwnProperty.call(body, "data")) return undefined;
  return body.data;
};

export const mapPincodeDetailsFromData = (data) => ({
  city: data?.city ?? "",
  state: data?.state ?? "",
});

const readPresentHistoryKey = (source, keys = []) => {
  if (!source || typeof source !== "object") return undefined;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const value = source[key];
    if (value === undefined) continue;
    return value;
  }
  return undefined;
};

const asTrimmedHistoryString = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
};

/** prerna.chugh → Prerna Chugh. Uses the API modify_id; no fixed names. */
const formatHrModifyId = (value) => {
  const raw = asTrimmedHistoryString(value);
  if (!raw) return "";
  const local = raw.includes("@") ? raw.split("@")[0] : raw;
  return local
    .split(/[._-]+|\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const hrHistoryItemType = (item) =>
  String(item?.type || "")
    .trim()
    .toLowerCase();

const readFirstHistoryText = (source, keys = []) => {
  if (!source || typeof source !== "object") return "";
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const text = asTrimmedHistoryString(source[key]);
    if (text) return text;
  }
  return "";
};

const looksLikeHrEditHistoryEntry = (item) => {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false;
  return (
    Object.prototype.hasOwnProperty.call(item, "old_value") ||
    Object.prototype.hasOwnProperty.call(item, "oldValue") ||
    Object.prototype.hasOwnProperty.call(item, "new_value") ||
    Object.prototype.hasOwnProperty.call(item, "newValue") ||
    Object.prototype.hasOwnProperty.call(item, "reason") ||
    Object.prototype.hasOwnProperty.call(item, "edited_by") ||
    Object.prototype.hasOwnProperty.call(item, "editedBy") ||
    Object.prototype.hasOwnProperty.call(item, "changed_by") ||
    Object.prototype.hasOwnProperty.call(item, "changedBy") ||
    Object.prototype.hasOwnProperty.call(item, "edited_at") ||
    Object.prototype.hasOwnProperty.call(item, "editedAt") ||
    Object.prototype.hasOwnProperty.call(item, "changed_at") ||
    Object.prototype.hasOwnProperty.call(item, "changedAt") ||
    Object.prototype.hasOwnProperty.call(item, "modified_by") ||
    Object.prototype.hasOwnProperty.call(item, "modified_at")
  );
};

const extractHrEditHistoryList = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];

  const nestedCandidates = [
    data.history,
    data.edits,
    data.items,
    data.records,
    data.entries,
    data.hr_edit_history,
    data.edit_history,
  ];
  for (const candidate of nestedCandidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  if (looksLikeHrEditHistoryEntry(data)) return [data];
  return [];
};

/**
 * Map get_hr_edit_history body.data into UI-safe shape.
 * The history array is the source of truth. candidate_value and current_value
 * are not turned into extra rows when history already lists those entries.
 */
export const normalizeHrEditHistoryResponse = (data) => {
  const list = extractHrEditHistoryList(data);
  const hasTypedHistory = list.some((item) => hrHistoryItemType(item));
  const candidateItem = list.find(
    (item) => hrHistoryItemType(item) === "candidate",
  );

  let candidateValue = "";
  if (candidateItem) {
    candidateValue = asTrimmedHistoryString(
      readPresentHistoryKey(candidateItem, [
        "value",
        "candidate_value",
        "new_value",
        "newValue",
      ]),
    );
  } else if (!hasTypedHistory) {
    candidateValue = asTrimmedHistoryString(
      readPresentHistoryKey(data && !Array.isArray(data) ? data : null, [
        "candidate_value",
        "candidate_entered",
        "candidate_entered_value",
        "original_value",
        "originalValue",
      ]),
    );
  }

  const changes = list
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => hrHistoryItemType(item) !== "candidate")
    .map(({ item, index }) => {
      if (!item || typeof item !== "object") {
        return {
          key: `entry-${index}`,
          oldValue: "",
          newValue: "",
          changedBy: "",
          changedAt: "",
          reason: "",
          raw: item,
        };
      }

      const changedBy = formatHrModifyId(
        readFirstHistoryText(item, [
          "modify_id",
          "modifyId",
          "changed_by",
          "changedBy",
          "edited_by",
          "editedBy",
          "modified_by",
          "modifiedBy",
          "hr_name",
          "user_name",
          "updated_by",
        ]),
      );

      return {
        key: `entry-${index}`,
        oldValue: asTrimmedHistoryString(
          readPresentHistoryKey(item, [
            "old_value",
            "oldValue",
            "previous_value",
            "from_value",
          ]),
        ),
        newValue: asTrimmedHistoryString(
          readPresentHistoryKey(item, [
            "new_value",
            "newValue",
            "changed_value",
            "to_value",
            "value",
          ]),
        ),
        changedBy,
        changedAt: readFirstHistoryText(item, [
          "date",
          "changed_at",
          "changedAt",
          "edited_at",
          "editedAt",
          "modified_at",
          "modifiedAt",
          "timestamp",
          "created_at",
          "updated_at",
        ]),
        reason: asTrimmedHistoryString(
          readPresentHistoryKey(item, ["reason", "edit_reason", "remarks"]),
        ),
        raw: item,
      };
    });

  if (!candidateValue && !hasTypedHistory) {
    const firstOld = changes.find((entry) => entry.oldValue);
    if (firstOld) candidateValue = firstOld.oldValue;
  }

  return {
    candidateValue,
    changes,
    raw: data,
  };
};

export const hrEditHistoryIdentity = (
  sectionKey,
  fieldKey,
  recordIndex = null,
) => {
  const record =
    recordIndex == null || recordIndex === "" ? "" : `:${recordIndex}`;
  return `${sectionKey || ""}:${fieldKey || ""}${record}`;
};

export const rejectMessageFromThunk = (error, fallback = "Request failed") => {
  if (typeof error === "string" && error.trim()) return error.trim();
  if (error?.message) return String(error.message);
  return fallback;
};
